import { useState, useCallback } from 'react'
import { BookOpen, FileText, CheckCircle, AlertTriangle, XCircle, ExternalLink, Sparkles, Check, Search, Pencil, Globe, Trash2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { mockEvidence } from '@/data/mockEvidence'
import type { EvidenceSource } from '@/data/mockEvidence'
import type { ComplianceStatus } from '@/components/workbench/ComplianceDecorationPlugin'
import type { LibraryItem } from '@/data/mock'
import { DiscoveryModal } from './DiscoveryModal'

interface EvidenceLockerProps {
  selectedClaimId: string | null
  activeSourceId?: string | null
  onHighlightClause?: (clauseId: string | null) => void
  onImportToLibrary?: (item: LibraryItem) => void
  onSourceReject?: (claimId: string, sourceId: string, page: number) => void
}

const statusConfig: Record<ComplianceStatus, {
  label: string
  className: string
  icon: typeof CheckCircle
}> = {
  verified: {
    label: 'Verified',
    className: 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100',
    icon: CheckCircle,
  },
  partial: {
    label: 'Partial Match',
    className: 'bg-amber-100 text-amber-800 hover:bg-amber-100',
    icon: AlertTriangle,
  },
  unverified: {
    label: 'Unverified',
    className: 'bg-rose-100 text-rose-800 hover:bg-rose-100',
    icon: XCircle,
  },
  contradicted: {
    label: 'Contradicted',
    className: 'bg-red-100 text-red-800 hover:bg-red-100',
    icon: XCircle,
  },
  unsupported: {
    label: 'Unsupported',
    className: 'bg-amber-100 text-amber-800 hover:bg-amber-100',
    icon: AlertTriangle,
  },
}

function SourceCard({
  src,
  index,
  isActive,
  onHighlightClause,
  onReject,
}: {
  src: EvidenceSource
  index: number
  isActive: boolean
  onHighlightClause?: (clauseId: string | null) => void
  onReject?: (sourceId: string, page: number) => void
}) {
  const [confirmed, setConfirmed] = useState(false)
  const [editing, setEditing] = useState(false)

  const handleToggleConfirm = () => {
    setConfirmed((prev) => !prev)
  }

  const handleStartEdit = () => {
    setEditing(true)
  }

  const handleReject = (e: React.MouseEvent) => {
    e.stopPropagation()
    onReject?.(src.sourceId, src.page)
  }

  const handleCardClick = () => {
    if (isActive) {
      onHighlightClause?.(null)
    } else {
      onHighlightClause?.(src.sourceId)
    }
  }

  return (
    <div
      className={`rounded-lg border p-3 min-w-0 cursor-pointer transition-all ${
        isActive
          ? 'border-indigo-400 bg-indigo-50/40 ring-1 ring-indigo-300'
          : confirmed
            ? 'border-emerald-300 bg-emerald-50/20'
            : 'border-border hover:border-indigo-300'
      }`}
      onClick={handleCardClick}
    >
      {/* Source header */}
      <div className="flex items-center gap-1.5 mb-2 min-w-0">
        <span className="text-xs font-bold text-blue-600 shrink-0">[{index}]</span>
        <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        <span className="text-sm font-medium text-foreground truncate min-w-0">
          {src.source}
        </span>
        <span className="text-xs text-muted-foreground shrink-0">
          p. {src.page}
        </span>
        {confirmed && (
          <Badge className="ml-auto shrink-0 gap-0.5 bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px] px-1.5 py-0">
            <Check className="h-2.5 w-2.5" />
            Verified
          </Badge>
        )}
        <button
          onClick={handleReject}
          className={`${confirmed ? '' : 'ml-auto'} shrink-0 p-0.5 rounded text-slate-300 hover:text-rose-500 hover:bg-rose-50 transition-colors`}
          title="Remove this source"
        >
          <Trash2 className="h-3 w-3" />
        </button>
      </div>

      {/* AI Insight — subtle semantic bridge */}
      {src.explanation && (
        <div className="rounded-md px-2.5 py-2 mb-2 bg-slate-50 border border-slate-100">
          <div className="flex items-center gap-1 mb-0.5">
            <Sparkles className="h-2.5 w-2.5 text-slate-400" />
            <span className="text-[10px] font-medium uppercase tracking-wider text-slate-400">
              AI Insight
            </span>
          </div>
          <p className="text-xs text-slate-500 leading-relaxed break-words">
            {src.explanation}
          </p>
        </div>
      )}

      {/* Raw snippet */}
      <div className="bg-slate-100 rounded-md p-3 w-full">
        <p className="text-sm italic text-slate-600 leading-relaxed break-words">
          {src.snippet}
        </p>
      </div>

      {/* Row 1: View in PDF — primary auditing action */}
      <div className="mt-2" onClick={(e) => e.stopPropagation()}>
        <Button
          variant="outline"
          size="sm"
          className="w-full gap-1.5 text-xs h-7"
        >
          <ExternalLink className="h-3 w-3" />
          View in PDF
        </Button>
      </div>

      {/* Row 2: Edit Source + Confirm toggle */}
      {editing ? (
        <div className="mt-2 space-y-2" onClick={(e) => e.stopPropagation()}>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <input
              placeholder="Search for a different source..."
              className="h-8 w-full rounded-md border border-border bg-white pl-7 pr-2 text-xs outline-none focus:ring-1 focus:ring-indigo-300"
            />
          </div>
          <Button
            size="sm"
            variant="outline"
            className="w-full text-xs h-7"
            onClick={() => setEditing(false)}
          >
            Cancel
          </Button>
        </div>
      ) : (
        <div className="flex gap-2 mt-2" onClick={(e) => e.stopPropagation()}>
          <Button
            size="sm"
            variant="outline"
            className="flex-1 gap-1 text-xs h-7"
            onClick={handleStartEdit}
          >
            <Pencil className="h-3 w-3" />
            Edit Source
          </Button>
          <Button
            size="sm"
            className={`flex-1 gap-1 text-xs h-7 ${
              confirmed
                ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 border border-emerald-300'
                : 'bg-emerald-600 hover:bg-emerald-700 text-white'
            }`}
            variant={confirmed ? 'outline' : 'default'}
            onClick={handleToggleConfirm}
          >
            <Check className="h-3 w-3" />
            {confirmed ? 'Unconfirm' : 'Confirm'}
          </Button>
        </div>
      )}
    </div>
  )
}

export function EvidenceLocker({ selectedClaimId, activeSourceId, onHighlightClause, onImportToLibrary, onSourceReject }: EvidenceLockerProps) {
  const [rejectedKeys, setRejectedKeys] = useState<Set<string>>(new Set())
  const [discoveryOpen, setDiscoveryOpen] = useState(false)
  const evidence = selectedClaimId ? mockEvidence[selectedClaimId] : null

  const handleReject = useCallback((sourceId: string, page: number) => {
    const key = `${sourceId}:${page}`
    setRejectedKeys((prev) => new Set(prev).add(key))
    if (selectedClaimId) {
      onSourceReject?.(selectedClaimId, sourceId, page)
    }
  }, [selectedClaimId, onSourceReject])

  if (!evidence) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6 text-center">
        <BookOpen className="h-10 w-10 text-muted-foreground mb-3" />
        <h3 className="text-sm font-medium text-foreground mb-1">
          Evidence Locker
        </h3>
        <p className="text-xs text-muted-foreground">
          Select a highlighted sentence to view evidence.
        </p>
      </div>
    )
  }

  const config = statusConfig[evidence.status]
  const StatusIcon = config.icon
  const isUnverified = evidence.status === 'unverified'

  // Filter out rejected sources, then group by sourceId
  const activeSources = evidence.sources.filter(
    (src) => !rejectedKeys.has(`${src.sourceId}:${src.page}`)
  )

  const groupedSources = new Map<string, EvidenceSource[]>()
  for (const src of activeSources) {
    const existing = groupedSources.get(src.sourceId) ?? []
    existing.push(src)
    groupedSources.set(src.sourceId, existing)
  }

  // Assign display indices (simple sequential for grouped display)
  let sourceIndex = 0
  const sourceCards: { src: EvidenceSource; index: number }[] = []
  for (const [, srcs] of groupedSources) {
    sourceIndex++
    for (const src of srcs) {
      sourceCards.push({ src, index: sourceIndex })
    }
  }

  return (
    <div className="flex flex-col h-full min-h-0 min-w-0">
      {/* Scrollable cards area — min-h-0 is critical for flex shrinking */}
      <ScrollArea className="flex-1 min-h-0 overflow-hidden">
        <div className="flex flex-col gap-3 p-4 min-w-0 max-w-full overflow-hidden">
          {/* Card 1: The Claim */}
          <div className="rounded-lg border border-border p-3">
            <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
              Claim
            </h3>
            <p className="text-sm text-foreground leading-relaxed break-words">
              {evidence.text}
            </p>
          </div>

          {/* Card 2: The Verdict */}
          <div className="rounded-lg border border-border p-3">
            <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
              Verdict
            </h3>
            <Badge className={`gap-1.5 ${config.className}`}>
              <StatusIcon className="h-3.5 w-3.5" />
              {config.label}
            </Badge>
          </div>

          {/* Source Cards or Discovery Card */}
          {isUnverified ? (
            <>
              {/* Still show the weak source if one exists */}
              {sourceCards.length > 0 && (
                <>
                  <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-0">
                    Closest Match (Insufficient)
                  </h3>
                  {sourceCards.map(({ src, index }, i) => (
                    <SourceCard
                      key={`${src.sourceId}-${src.page}-${i}`}
                      src={src}
                      index={index}
                      isActive={activeSourceId === src.sourceId}
                      onHighlightClause={onHighlightClause}
                      onReject={handleReject}
                    />
                  ))}
                </>
              )}

              {/* Discovery button for external search */}
              <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-0">
                Find Evidence
              </h3>
              <Button
                variant="outline"
                className="w-full gap-1.5"
                onClick={() => setDiscoveryOpen(true)}
              >
                <Globe className="h-4 w-4" />
                Search External Evidence
              </Button>
            </>
          ) : (
            <>
              <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-0">
                {activeSources.length === 1 ? 'Source' : `Sources (${activeSources.length})`}
              </h3>
              {sourceCards.map(({ src, index }, i) => (
                <SourceCard
                  key={`${src.sourceId}-${src.page}-${i}`}
                  src={src}
                  index={index}
                  isActive={activeSourceId === src.sourceId}
                  onHighlightClause={onHighlightClause}
                  onReject={handleReject}
                />
              ))}
            </>
          )}

          {/* Bottom spacer so last card isn't cramped */}
          <div className="pb-2" />
        </div>
      </ScrollArea>

      {evidence && (
        <DiscoveryModal
          open={discoveryOpen}
          onOpenChange={setDiscoveryOpen}
          claimText={evidence.text}
          onImportToLibrary={onImportToLibrary}
        />
      )}
    </div>
  )
}
